import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts"
import { DollarSign } from "lucide-react"
import api from "../../services/api"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group"
import { Skeleton } from "../ui/skeleton"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart"

const periods = [
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "ytd", label: "YTD" },
]

const chartConfig = {
  revenue: {
    label: "Revenue",
    color: "hsl(var(--chart-2))",
  },
}

export default function RevenueCard() {
  const [period, setPeriod] = useState("month")

  const { data, isLoading, error } = useQuery({
    queryKey: ["appstore-revenue", period],
    queryFn: () => api.getAppStoreRevenue(period),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  })

  // Format chart data
  const chartData = data?.chartData?.map(item => ({
    date: new Date(item.date).toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric" 
    }),
    revenue: item.revenue,
    fullDate: item.date,
  })) || []

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: data?.currency || "USD",
      minimumFractionDigits: 2,
    }).format(value || 0)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>Revenue</CardTitle>
              <CardDescription>
                Developer proceeds over time
              </CardDescription>
            </div>
          </div>
          <ToggleGroup 
            type="single" 
            value={period} 
            onValueChange={(val) => val && setPeriod(val)}
            size="sm"
          >
            {periods.map((p) => (
              <ToggleGroupItem key={p.value} value={p.value}>
                {p.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[300px] w-full" />
        ) : error ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <p>Failed to load revenue data</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <p>No revenue data available for this period</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Total */}
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-green-600">
                {formatCurrency(data?.totalRevenue)}
              </span>
              <span className="text-sm text-muted-foreground">total revenue</span>
            </div>

            {/* Chart */}
            <ChartContainer config={chartConfig} className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(142.1 76.2% 36.3%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(142.1 76.2% 36.3%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }} 
                    tickLine={false}
                    axisLine={false}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }} 
                    tickLine={false}
                    axisLine={false}
                    className="text-muted-foreground"
                    width={50}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <ChartTooltip 
                    content={
                      <ChartTooltipContent 
                        formatter={(value) => formatCurrency(value)}
                      />
                    }
                    cursor={{ stroke: "hsl(var(--muted-foreground))", strokeDasharray: "4 4" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(142.1 76.2% 36.3%)"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>

            {/* Date range */}
            {data?.startDate && data?.endDate && (
              <p className="text-xs text-muted-foreground text-center">
                {new Date(data.startDate).toLocaleDateString()} – {new Date(data.endDate).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
